/** API base — Same-Origin /api in dev (Vite proxy) and prod (Caddy). */
export const API_BASE = import.meta.env.VITE_API_BASE ?? "/api";

export interface LoginResult {
  access_token: string;
  refresh_token: string;
  sub: string;
}

export interface MeResult {
  sub: string;
  email: string | null;
  role: string | null;
  created_at?: number | null;
}

async function parseError(resp: Response): Promise<string> {
  const body = await resp.text();
  try {
    const j = JSON.parse(body) as { error?: string };
    if (j.error) return j.error;
  } catch {
    /* keep raw */
  }
  return body || `HTTP ${resp.status}`;
}

export async function authLogin(email: string, password: string): Promise<LoginResult> {
  const resp = await fetch(`${API_BASE}/auth/login`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password, cookie_session: true }),
  });
  if (!resp.ok) throw new Error(await parseError(resp));
  return (await resp.json()) as LoginResult;
}

export async function authRegister(email: string, password: string): Promise<LoginResult & { requires_activation?: boolean }> {
  const resp = await fetch(`${API_BASE}/auth/register`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password, cookie_session: true }),
  });
  if (!resp.ok) throw new Error(await parseError(resp));
  return (await resp.json()) as LoginResult & { requires_activation?: boolean };
}

export async function authMe(): Promise<MeResult | null> {
  const resp = await fetch(`${API_BASE}/auth/me`, { credentials: "include" });
  if (resp.status === 401 || resp.status === 403) return null;
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  return (await resp.json()) as MeResult;
}

export async function authLogout(): Promise<void> {
  await fetch(`${API_BASE}/auth/logout`, { method: "POST", credentials: "include" });
}

export async function requestPasswordReset(email: string): Promise<void> {
  const resp = await fetch(`${API_BASE}/auth/password-reset/request`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  if (!resp.ok) throw new Error(await parseError(resp));
}

export async function completePasswordReset(token: string, password: string): Promise<void> {
  const resp = await fetch(`${API_BASE}/auth/password-reset/complete`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, password }),
  });
  if (!resp.ok) throw new Error(await parseError(resp));
}

export async function changePassword(
  oldPassword: string,
  newPassword: string,
  accessToken?: string | null,
): Promise<LoginResult> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  const resp = await fetch(`${API_BASE}/auth/password/change`, {
    method: "POST",
    credentials: "include",
    headers,
    body: JSON.stringify({ old_password: oldPassword, new_password: newPassword }),
  });
  if (!resp.ok) throw new Error(await parseError(resp));
  return (await resp.json()) as LoginResult;
}
